import * as Sentry from '@sentry/nextjs';

import { ERROR_SEVERITY, type TErrorSeverity } from '@/shared/constants/sentry-constants';

let isAlreadySet = false;
export function setSentryUser({ id, email, name }: { id: string; email: string; name: string }) {
	try {
		if (isAlreadySet) return;
		isAlreadySet = true;
		Sentry.setUser({ id, email, name });
	} catch (error) {
		console.error('Error setting sentry user', error);
	}
}

type SentryErrorSeverity = 'critical' | 'high' | 'medium' | 'low';

type CaptureCustomSentryErrorOptions = {
	/**
	 * Severity level. Maps to Sentry levels:
	 * - critical → 'fatal'
	 * - high     → 'error'
	 * - medium   → 'warning'
	 * - low      → 'info'
	 * @default 'high'
	 */
	severity?: SentryErrorSeverity;
	/** Sentry tags — key/value pairs that are indexed and searchable */
	tags?: Record<string, string | number | boolean>;
	/** Additional context objects shown in the Sentry issue detail */
	context?: Record<string, Record<string, unknown>>;
	/** Override fingerprint for manual grouping */
	fingerprint?: string[];
};

const SEVERITY_MAP: Record<SentryErrorSeverity, TErrorSeverity> = {
	critical: ERROR_SEVERITY.CRITICAL as TErrorSeverity,
	high: ERROR_SEVERITY.HIGH as TErrorSeverity,
	medium: ERROR_SEVERITY.MEDIUM as TErrorSeverity,
	low: ERROR_SEVERITY.LOW as TErrorSeverity,
};

/**
 * Capture a custom error or message to Sentry with structured context.
 *
 * Usage examples:
 *
 * ```ts
 * // Capture an actual Error object (preferred — preserves stack trace)
 * captureCustomSentryError(error, 'Message send failed', {
 *   severity: 'critical',
 *   tags: { submissionId, formId },
 *   context: { 'Request Details': { submissionId, formId } },
 * });
 *
 * // Capture a message-only event (no error object available)
 * captureCustomSentryError(null, 'Invalid submission ID in URL', {
 *   severity: 'medium',
 *   tags: { badId: submissionId },
 * });
 * ```
 */
export function captureCustomSentryError(
	error: unknown,
	message: string,
	options: CaptureCustomSentryErrorOptions = {}
): void {
	const { severity = 'high', tags = {}, context = {}, fingerprint } = options;
	const sentryLevel = SEVERITY_MAP[severity];

	try {
		Sentry.withScope((scope) => {
			scope.setLevel(sentryLevel);

			Object.entries(tags).forEach(([key, value]) => {
				scope.setTag(key, String(value));
			});

			Object.entries(context).forEach(([key, value]) => {
				scope.setContext(key, value);
			});

			if (fingerprint) {
				scope.setFingerprint(fingerprint);
			}

			if (error) {
				// Attach raw error details as extra context
				scope.setContext('Error Details', {
					message: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error ? error.stack : undefined,
					raw: String(error),
				});
				Sentry.captureException(error instanceof Error ? error : new Error(message));
			} else {
				Sentry.captureMessage(message, sentryLevel);
			}
		});
	} catch (sentryError) {
		console.error('Failed to capture error to Sentry:', sentryError);
	}
}
